import React, { useState, useEffect, useRef } from "react";
import {
  Avatar,
  Button,
  Input,
  Form,
  Select,
  Row,
  Col,
  Space,
  Spin,
  Tooltip,
  Divider,
  message,
  Popconfirm,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import UserIcon from "../../../../assets/images/icons/userLogo.svg";
import { useLocation, useParams } from "react-router-dom";
import { useStore } from "../../../../store";
import { TAB_KEYS } from "../../../../config";
import { useTab } from "../../../../custom-hooks/useTab";
import { useConnectsuForm } from "../../../../custom-hooks/useConnectsuForm";
import { getFormFieldValue } from "../../../../utils";
import { sortDataByOrder } from "../../../../utils/helper"
import "../../../slider/imageSlider.scss";
import ReactPlayer from "react-player/youtube";
import SynthesiaVideo from "./SynthesiaVideo";
import "./WhyWorkForUs.scss";
import { deleteRequest, getRequest, putRequest } from "../../../../api/http";
import userServiceApi from "../../../../api/userServiceApi";
import { toast } from "react-toastify";
import reArrangeOrder from "../../../common/DragAndDropList/rearrangeHelper";
import {
  DragAndDropList,
  GifCreator,
  GifResultPreview,
  createEmptyGifState,
  hydrateGifCreatorState,
} from "../../../common";

const { TextArea } = Input;
const MAX_CHARACTERS = 700;

const masterDataSectionEnums = {
  WHY_WORK_FOR_US: 'WHY_WORK_FOR_US',
}
export default function WhyWorkForUs({ dataLoader, form }) {
  const { state } = useLocation();
  const inputRef = useRef(null);

  const tabKey = state;
  const { tabData } = useStore();
  const whichTab = TAB_KEYS.whyWorkForUs;
  const { deleteSingleItem, postLoader } = useTab();
  const { updateImage } = useConnectsuForm();
  const { micrositeId } = useParams();
  const tabConfigId = localStorage?.getItem("selectedTabId");

  const [loader, setLoader] = useState(true);
  const [customName, setName] = useState("");
  const [formChanged, setFormChanged] = useState(false);
  const { updatedValues } = useStore();

  const [canAddNew, setCanAddNew] = useState(true);
  const [hasUnsavedItem, setHasUnsavedItem] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [whyWorkForUsList, setWhyWorkForUsList] = useState(null);
  const [whyWorkForUsDefaultList, setWhyWorkForUsDefaultList] = useState(null);

  // ✅ Empty Row Template
  const emptyRow = {
    id: undefined,
    title: undefined,
    description: "",
    media: {},
    mediaLink: { mediaType: undefined, url: "", gifCreator: undefined },
  };

  const withGifDefaults = (rows) => {
    if (!Array.isArray(rows)) return rows;
    return rows.map((row) => {
      if (row?.mediaLink?.mediaType === "GIF") {
        return {
          ...row,
          mediaLink: {
            ...row.mediaLink,
            gifCreator: hydrateGifCreatorState(row.mediaLink),
          },
        };
      }
      return row;
    });
  };

  const getDefaultSectionTitles = async () => {
    try {
      const res = await getRequest(
        userServiceApi.getDefaultSectionTitles(masterDataSectionEnums.WHY_WORK_FOR_US)
      )
      if (res?.data) {
        setWhyWorkForUsDefaultList(res.data);
      }
    } catch (error) {
      toast.error(error.error);
    }
  }

  const getSectionTitles = async () => {
    try {
      const res = await getRequest(
        userServiceApi.getSectionTitles(micrositeId, tabConfigId)
      );
      if (res?.data) {
        setWhyWorkForUsList(res.data);
      }
    } catch (error) {
      toast.error(error.error);
    }
  };

  const addSectionTitle = async (title) => {
    try {
      await putRequest(
        userServiceApi.addSectionTitle(micrositeId, tabConfigId),
        [{ title }]
      );
      setName("");
      toast.success("Title added successfully");
      getSectionTitles();
    } catch (error) {
      toast.error(error.error);
    }
  };

  const deleteSectionTitle = async (titleId) => {
    try {
      await deleteRequest(userServiceApi.deleteSectionTitle(titleId));
      toast.success("Title deleted successfully");
      getSectionTitles();
    } catch (error) {
      toast.error(error.error);
    }
  };

  useEffect(() => {
    if (micrositeId && tabConfigId) {
      getSectionTitles();
      getDefaultSectionTitles();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [micrositeId, tabConfigId]);

  useEffect(() => {
    localStorage.setItem("tab", "WHY_WORK");
    refreshUnsavedState();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const isNotEmpty = Object.keys(updatedValues).length > 0;
    setFormChanged(isNotEmpty);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Object.keys(updatedValues).length]);

  // ✅ Refresh Unsaved State
  const refreshUnsavedState = () => {
    const currentList = form.getFieldValue(whichTab) || [];
    const unsavedItem = currentList.find((item) => {
      const hasData =
        item?.title?.trim() ||
        item?.description?.trim() ||
        item?.mediaLink?.url?.trim() ||
        item?.mediaLink?.mediaType ||
        item?.media ||
        item?.mediaLink?.gifCreator?.images?.length ||
        item?.mediaLink?.gifCreator?.generatedFile;
      return !item?.id && hasData;
    });

    setCanAddNew(!unsavedItem);
    setHasUnsavedItem(!!unsavedItem);
  };

  // ✅ Handle Add More - NEW ITEMS AT TOP
  const handleAddMore = () => {
    if (!canAddNew) {
      message.warning(
        "You must save or clear the current item before adding a new one."
      );
      return;
    }

    const currentList = form.getFieldValue(whichTab) || [];
    const newList = [emptyRow, ...currentList]; // Add new row on top
    form.setFieldsValue({ [whichTab]: newList });

    setCanAddNew(false);
    setHasUnsavedItem(true);

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ✅ Handle Clear - CLEAR ONLY THE FIRST (NEWEST) UNSAVED ITEM
  const handleClear = () => {
    const currentList = form.getFieldValue(whichTab) || [];

    // Find the first unsaved item (index 0, since new items are added at top)
    const firstUnsavedIndex = currentList.findIndex((item) => !item?.id);

    if (firstUnsavedIndex === -1) {
      message.warning("No unsaved item to clear.");
      return;
    }

    // Clear ONLY the first unsaved item
    const updatedList = currentList.map((item, idx) =>
      idx === firstUnsavedIndex
        ? {
          ...item,
          title: "",
          description: "",
          media: {},
          blob: undefined,
          mediaLink: { mediaType: undefined, url: "", gifCreator: undefined },
        }
        : item
    );

    form.setFieldsValue({ [whichTab]: updatedList });
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (tabData?.["why-work"]?.length === 0) {
        setHasUnsavedItem(true);
      } else {
        setHasUnsavedItem(false);
      }
    }, 500);

    return () => clearTimeout(timer); // cleanup for safety
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabData?.["why-work"]]);

  const renderMedia = (dataItem, rowIndex) => {
    const commonStyles = {
      display: "flex",
      justifyContent: "center",
    };

    if (
      dataItem?.mediaLink?.mediaType === "SOCIAL_MEDIA" &&
      dataItem.mediaLink.url
    ) {
      return (
        <div className={`iframe-comp ${dataItem?.mediaLink?.mediaType}`} style={commonStyles}>
          <ReactPlayer
            controls={true}
            url={dataItem.mediaLink.url}
            width="100%"
            height="100%"
          />
        </div>
      );
    } else if (
      dataItem?.mediaLink?.mediaType === "VIMEO_MEDIA" &&
      dataItem.mediaLink.url
    ) {
      return (
        <div className={`iframe-comp ${dataItem?.mediaLink?.mediaType}`} style={commonStyles}>
          <iframe
            title="Embedded video"
            src={dataItem.mediaLink.url}
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            width="100%"
            height="100%"
            style={{ backgroundColor: "black" }}
          />
        </div>
      );
    } else if (
      dataItem?.mediaLink?.mediaType === "IMAGE" &&
      dataItem.mediaLink.url
    ) {
      return (
        <div className={`iframe-comp ${dataItem?.mediaLink?.mediaType}`} style={commonStyles}>
          <img
            src={dataItem?.mediaLink?.url}
            alt=""
            className="c1 carousel-image"
            style={{ maxWidth: "100%", maxHeight: "100%" }}
          />
        </div>
      );
    } else if (
      dataItem?.mediaLink?.mediaType === "GIF"
    ) {
      const gifState = dataItem.mediaLink.gifCreator || {};
      const gifUrl = gifState.previewUrl || dataItem.mediaLink.url;
      return (
        <GifResultPreview
          url={gifUrl}
          fileName={gifState.fileName}
          loading={!!gifState.generating}
          showEdit={!!gifUrl && gifState.isEditing === false}
          onEdit={() => {
            const current = form.getFieldValue([
              whichTab,
              rowIndex,
              "mediaLink",
              "gifCreator",
            ]);
            form.setFieldValue(
              [whichTab, rowIndex, "mediaLink", "gifCreator"],
              { ...createEmptyGifState(), ...current, isEditing: true }
            );
          }}
        />
      );
    } else if (
      dataItem?.mediaLink?.mediaType === "SYNTHESIA_MEDIA" &&
      dataItem.mediaLink.url
    ) {
      return (
        <div className={`iframe-comp ${dataItem?.mediaLink?.mediaType}`} style={commonStyles}>
          <SynthesiaVideo key={dataItem.id} videoData={dataItem} />
        </div>
      );
    }

    return null;
  };

  const findValue = (i, key) => {
    return getFormFieldValue(form, whichTab, i, key);
  };

  const SelectChangeHandler = (e, i) => {
    form.setFieldValue([whichTab, i, "mediaLink", "url"], "");
    form.setFieldValue([whichTab, i, "blob"], undefined);
    if (e === "GIF") {
      form.setFieldValue(
        [whichTab, i, "mediaLink", "gifCreator"],
        createEmptyGifState()
      );
    } else {
      form.setFieldValue([whichTab, i, "mediaLink", "gifCreator"], undefined);
    }
  };

  useEffect(() => {
    const rows =
      tabData && tabData[whichTab] && tabData[whichTab].length > 0
        ? withGifDefaults(sortDataByOrder(tabData[whichTab]))
        : [emptyRow];
    form.resetFields();
    form.setFieldsValue({ [whichTab]: rows });
    refreshUnsavedState();
    setTimeout(() => {
      setLoader(false);
    }, 1000);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabData[whichTab]]);

  const onNameChange = (event) => {
    setName([event.target.value]);
  };

  // ✅ Subscribe to form changes for unsaved state
  useEffect(() => {
    const unsubscribe = form.subscribe?.(() => refreshUnsavedState());
    return () => unsubscribe?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  if (dataLoader || postLoader || loader) {
    return (
      <div className="data-loader">
        <Spin tip="Loading" size="large" />
      </div>
    );
  } else {
    return (
      <div className="newContantStyle">
        <Row className="rowBody" gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
          {tabKey === whichTab ? (
            <>
              <Col className="gutter-row tabContent" span={24}>
                <Form.List
                  name={whichTab}
                  initialValue={
                    tabData && tabData[whichTab] && tabData[whichTab].length > 0
                      ? withGifDefaults(sortDataByOrder(tabData[whichTab]))
                      : [emptyRow]
                  }
                >
                  {(fields, { add, remove, move }) => {
                    return (
                    <>
                      <Col
                        className="gutter-row tabContent"
                        xs={24}
                        sm={24}
                        md={14}
                        lg={14}
                        style={{ paddingRight: "25px" }}
                      >
                        <div className="inputBody">
                          <label></label>
                          <div style={{ textAlign: "right" }}>
                            {hasUnsavedItem && (
                              <Button
                                className="cancelBtn"
                                type="button"
                                onClick={handleClear}
                              >
                                Clear
                              </Button>
                            )}
                            <Button className="greenBtn" onClick={handleAddMore}>
                              Add <PlusOutlined />
                            </Button>
                          </div>
                        </div>
                      </Col>

                      <Col
                        className="gutter-row tabContent"
                        xs={24}
                        sm={24}
                        md={10}
                        lg={10}
                      >
                      </Col>

                      <DragAndDropList
                        fields={fields}
                        move={move}
                        form={form}
                        fieldName={whichTab}
                        onReorder={(payload) => reArrangeOrder(userServiceApi.reArrangeCardsData({ micrositeId, tabConfigId }), payload)}
                        renderItem={({ key, name, ...restField }, i) => (
                          <div className="card-item">
                          <Col
                            className="gutter-row tabContent"
                            xs={24}
                            sm={24}
                            md={14}
                            lg={14}
                            style={{ paddingRight: "25px" }}
                          >
                            <div className="d-flex">
                              <Form.Item
                                className="custom-work"
                                {...restField}
                                label="Why work For us"
                                labelCol={{ span: 5 }}
                                name={[name, "title"]}
                                rules={[
                                  {
                                    required: true,
                                    message: "Please select or add a reason",
                                  },
                                ]}
                              >
                                <Select
                                  name={[name, "title"]}
                                  placeholder="Click to select from drop down menu"
                                  dropdownRender={(menu) => (
                                    <>
                                      {menu}
                                      <Divider style={{ margin: "8px 0" }} />
                                      <Space style={{ width: "100%", padding: "0 8px 4px" }}>
                                        <Input
                                          placeholder="Please enter item"
                                          ref={inputRef}
                                          value={customName}
                                          onChange={onNameChange}
                                          maxLength={40}
                                          showCount={{
                                            formatter: ({count}) => `${count}/40`,
                                          }}
                                        />
                                        <Button
                                          type="text"
                                          icon={<PlusOutlined />}
                                          onClick={
                                            () => {
                                              if (!customName) {
                                                toast.error("Reason cannot be empty");
                                                return;
                                              }
                                              if (
                                                String(customName[0]).trim()
                                                  .length > 0
                                              ) {
                                                addSectionTitle(customName[0]);
                                              }
                                            }
                                          }
                                        >
                                          Add another reason
                                        </Button>
                                      </Space>
                                    </>
                                  )}
                                >
                                  {Array.isArray(whyWorkForUsList) &&
                                    whyWorkForUsList.map((item) => (
                                      <Select.Option
                                        value={item?.title}
                                        key={item?.id}
                                      >
                                        <div className="why-work-options">
                                          <div
                                            className="why-work-options-title"
                                            title={item?.title || ""}
                                          >
                                            {item?.title || ""}
                                          </div>
                                          <div
                                            className="why-work-option-delete-btn"
                                            title="Delete item"
                                          >
                                            <Popconfirm
                                              title="Are you sure?"
                                              onConfirm={(e) => {
                                                e.stopPropagation();
                                                const currentTitle =
                                                  form.getFieldValue([
                                                    whichTab,
                                                    i,
                                                    "title",
                                                  ]);
                                                if (
                                                  currentTitle === item?.title
                                                ) {
                                                  form.setFieldValue([whichTab, i, "title"], "");
                                                  setFormChanged(true);
                                                }
                                                deleteSectionTitle(item?.id);
                                              }}
                                              okText="Yes"
                                              cancelText="No"
                                              zIndex={999999}
                                            >
                                              <DeleteOutlined
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                }}
                                              />
                                            </Popconfirm>
                                          </div>
                                        </div>
                                      </Select.Option>
                                    ))}
                                  {Array.isArray(whyWorkForUsDefaultList) &&
                                    whyWorkForUsDefaultList.map((item) => (
                                      <Select.Option
                                        value={item?.value}
                                        key={item?.id}
                                      >
                                        <div className="why-work-options">
                                          <div
                                            className="why-work-options-title"
                                            title={item?.value || ""}
                                          >
                                            {item?.value || ""}
                                          </div>
                                        </div>
                                      </Select.Option>
                                    ))}
                                </Select>
                              </Form.Item>

                              <Form.Item>
                                <div className="remove-btn">
                                  <DeleteOutlined
                                    title="Delete"
                                    onClick={() => {
                                      const id = form.getFieldValue([
                                        whichTab,
                                        i,
                                        "id",
                                      ]);
                                      if (id) {
                                        // Existing saved record → call delete API
                                        deleteSingleItem(whichTab, id, form);
                                      } else {
                                        // Newly added unsaved record → just remove from form list
                                        remove(name);
                                        setHasUnsavedItem(false);
                                        refreshUnsavedState();
                                      }
                                    }}
                                  />
                                </div>
                              </Form.Item>
                            </div>

                            {/* ✅ Description - Required */}
                            <Form.Item
                              label="Description"
                              {...restField}
                              labelCol={{ span: 5 }}
                              name={[name, "description"]}
                              rules={[
                                {
                                  required: true,
                                  message: "Please enter a description",
                                },
                                {
                                  max: MAX_CHARACTERS - 1,
                                  message: `Description should be less than ${MAX_CHARACTERS} characters`,
                                },
                              ]}
                            >
                              <TextArea
                                placeholder="Enter Description"
                                rows={5}
                                maxLength={MAX_CHARACTERS}
                                showCount
                              />
                            </Form.Item>

                            <div className="d-flex">
                              {/* ✅ Video or Image - Required */}
                              <Form.Item
                                {...restField}
                                name={[name, "mediaLink", "mediaType"]}
                                className="w-100"
                                label="Video or Image"
                                labelCol={{ span: 5 }}
                              // rules={[{ required: true, message: "Please select media type" }]}
                              >
                                <Select
                                  name={[name, "mediaLink", "mediaType"]}
                                  onChange={(e) => SelectChangeHandler(e, i)}
                                  placeholder="Select Option"
                                  allowClear
                                >
                                  <Select.Option value="VIMEO_MEDIA">
                                    Add Vimeo Link
                                  </Select.Option>
                                  <Select.Option value="SOCIAL_MEDIA">
                                    Add Youtube Link
                                  </Select.Option>
                                  <Select.Option value="SYNTHESIA_MEDIA">
                                    Add Synthesia Link
                                  </Select.Option>
                                  <Select.Option value="IMAGE">
                                    Upload Image
                                  </Select.Option>
                                  <Select.Option value="GIF">
                                    Create GIF
                                  </Select.Option>
                                </Select>
                              </Form.Item>
                              <Form.Item>
                                <div className="remove-btn">
                                  <Tooltip title="Upload Video link or image with size lesser than 2.5MB. For best results, use images and videos with a 16:9 aspect ratio">
                                    <QuestionCircleOutlined />
                                  </Tooltip>
                                </div>
                              </Form.Item>
                            </div>

                            {/* ✅ Youtube/Vimeo/ Image fields required based on selection */}
                            {findValue(i, "type") === "SOCIAL_MEDIA" ? (
                              <Form.Item
                                label="Youtube Link"
                                labelCol={{ span: 5 }}
                                {...restField}
                                name={[name, "mediaLink", "url"]}
                              // rules={[{ required: true, message: "Please enter Youtube link" }]}
                              >
                                <Input placeholder="Enter Youtube link" />
                              </Form.Item>
                            ) : findValue(i, "type") === "VIMEO_MEDIA" ? (
                              <Form.Item
                                label="Vimeo Link"
                                labelCol={{ span: 5 }}
                                {...restField}
                                name={[name, "mediaLink", "url"]}
                              // rules={[{ required: true, message: "Please enter Vimeo link" }]}
                              >
                                <Input placeholder="Enter Vimeo link" />
                              </Form.Item>
                            ) : findValue(i, "type") === "IMAGE" ? (
                              <Form.Item
                                {...restField}
                                label="Image"
                                colon={false}
                                name="media"
                                className="experienceImg"
                                labelCol={{ span: 5 }}
                              // rules={[{ required: true, message: "Please upload an image" }]}
                              >
                                {findValue(i, "blob") ? (
                                  <Avatar
                                    src={URL.createObjectURL(
                                      findValue(i, "blob")
                                    )}
                                    alt=""
                                  />
                                ) : findValue(i, "url") ? (
                                  <Avatar src={findValue(i, "url")} alt="" />
                                ) : (
                                  <Avatar src={UserIcon} />
                                )}
                                <label
                                  name="file"
                                  className="fileUploadBtn greenBtn"
                                >
                                  Upload Image
                                  <input
                                    accept="image/png, image/gif, image/jpeg"
                                    type="file"
                                    onChange={(e) => {
                                      updateImage(e, i, form, whichTab);
                                      // 🔁 Force re-render after image upload
                                      setTimeout(
                                        () => setRefreshKey((prev) => prev + 1),
                                        200
                                      );
                                    }}
                                  />
                                </label>
                                <Tooltip
                                  className="question"
                                  title={"Recommended image dimensions: 1280x720 pixels"}
                                >
                                  <QuestionCircleOutlined />
                                </Tooltip>
                              </Form.Item>
                            ) : findValue(i, "type") === "GIF" ? (
                              <Form.Item
                                {...restField}
                                label="Create GIF"
                                colon={false}
                                name={[name, "mediaLink", "gifCreator"]}
                                className="gif-creator-field"
                                labelCol={{ span: 5 }}
                                initialValue={createEmptyGifState()}
                              >
                                <GifCreator />
                              </Form.Item>
                            ) : findValue(i, "type") === "SYNTHESIA_MEDIA" ? (
                              <Form.Item
                                label="Synthesia Link"
                                labelCol={{ span: 5 }}
                                {...restField}
                                name={[name, "mediaLink", "url"]}
                              >
                                <Input />
                              </Form.Item>
                            ) : null}

                            {/* ✅ Save Button */}
                            <Form.Item
                              className="w-100"
                              style={{
                                display: "flex",
                                justifyContent: "end",
                              }}
                            >
                              {formChanged ? (
                                <Button
                                  className="saveBtn greenBtn"
                                  onClick={() => {
                                    form.submit();
                                    setCanAddNew(true);
                                    setHasUnsavedItem(false);
                                  }}
                                >
                                  Save
                                </Button>
                              ) : (
                                <Button
                                  className="saveBtn greenBtn"
                                  style={{
                                    opacity: "0.5",
                                    pointerEvents: "none",
                                  }}
                                >
                                  Save
                                </Button>
                              )}
                            </Form.Item>
                          </Col>

                          <Col
                            className="gutter-row tabContent"
                            xs={24}
                            sm={24}
                            md={10}
                            lg={10}
                          >
                            <Form.Item shouldUpdate noStyle>
                              {() => {
                                const row = form.getFieldValue(whichTab)?.[i];
                                if (!row) return null;
                                return (
                                  <div key={refreshKey}>
                                    {renderMedia(row, i)}
                                  </div>
                                );
                              }}
                            </Form.Item>
                          </Col>
                        </div>
                    )}
                  />
                    </>
                  );
                  }}
                </Form.List>
              </Col>
            </>
          ) : (
            <></>
          )}
        </Row>
      </div>
    );
  }
}
